import React from "react";
import classNames from "classnames";

import type {MuiIconExternalProps} from "../icons/MuiIcon";
import {Tooltip} from "../Tooltip";

import "./SettingsSecondaryNav.css";

export interface SettingsSecondaryNavItem {
    id: string;
    title: string;
    Icon: React.ComponentType<MuiIconExternalProps>;
    isActive: boolean;
    onClick: () => void;
}

export interface SettingsSecondaryNavSection {
    id: string;
    title: string;
    items: SettingsSecondaryNavItem[];
}

export interface SettingsSecondaryNavProps {
    sections: SettingsSecondaryNavSection[];
    isOverlay?: boolean;
}

export const SettingsSecondaryNav = ({sections, isOverlay = false}: SettingsSecondaryNavProps) => {
    return (
        <aside className={classNames("SettingsSecondaryNav", "theme-dark", {
            "SettingsSecondaryNav--overlay": isOverlay,
        })}>
            {sections.map((section) => (
                <React.Fragment key={section.id}>
                    <div className="SettingsSecondaryNav__section title-medium">
                        {section.title}
                    </div>
                    {section.items.map((item) => (
                        <SettingsSecondaryNavItemRow key={item.id} item={item}/>
                    ))}
                </React.Fragment>
            ))}
        </aside>
    );
};

const SettingsSecondaryNavItemRow = ({item}: {item: SettingsSecondaryNavItem}) => {
    const textRef = React.useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = React.useState(false);
    const Icon = item.Icon;

    React.useEffect(() => {
        if (textRef.current) {
            setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
        }
    }, [item.title]);

    return (
        <div
            className={classNames("SettingsSecondaryNav_item title-small", {
                "SettingsSecondaryNav_item--selected": item.isActive,
            })}
            onClick={item.onClick}
        >
            <Icon className="SettingsSecondaryNav_icon" size={24}/>
            <span ref={textRef} className="SettingsSecondaryNav_text">
                {isTruncated ? (
                    <Tooltip maxWidth={300} title={item.title}>
                        <span>{item.title}</span>
                    </Tooltip>
                ) : (
                    item.title
                )}
            </span>
        </div>
    );
};
